import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SearchBar } from '@components/index';
import MultiFieldFilterV2 from '@components/multi-field-filter/MultiFieldFilterV2';
import {
  FieldOptionObjV2,
  SelectAllModes,
  SelectedFilterValues
} from '@components/multi-field-filter/types';
import { FEATURE_FLAGS } from '@constants/feature';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { CustomField } from '@features/transactions/types';
import { IdLabelValue } from '@types';

import { LeadSource } from '../constants';
import { fieldOptions } from '../constants/leadList';
import {
  AdsSelectMode,
  setLeadListPage,
  StuckDaysFilterState
} from '../slices/leadSlice';
import {
  decodeDaysStuckFilter,
  encodeDaysStuckFilter,
  getDaysStuckSelectedCount,
  isDaysStuckFilterComplete
} from '../utils/daysStuckFilter';
import {
  buildListFilteredMixpanelPayload,
  BuildListFilteredPayloadParams
} from '../utils/leadAnalytics';
import { getDateRangeForType, mapDateRangeOptions } from '../utils/leadList';
import DaysStuckFilterPanel from './DaysStuckFilterPanel';

const isAdsDisabled = (selectedItems: SelectedFilterValues): boolean =>
  !selectedItems.source?.includes(LeadSource.FACEBOOK_ADS);

type LeadFiltersProps = {
  search: string;
  setSearch: (s: string) => void;
  sourceOptions: IdLabelValue[];
  statusOptions: IdLabelValue[];
  tagOptions: IdLabelValue[];
  adOptions: IdLabelValue[];
  selectedSources: string[];
  selectedStatusIds: string[];
  selectedTagIds: string[];
  selectedAssigneeIds: string[];
  selectedAdIds: string[];
  setSelectedSources: (s: string[]) => void;
  setSelectedStatusIds: (s: string[]) => void;
  setSelectedTagIds: (s: string[]) => void;
  setSelectedAssigneeIds: (s: string[]) => void;
  setSelectedAdIds: (s: string[]) => void;
  adsSelectMode: AdsSelectMode;
  setAdsSelectMode: (mode: AdsSelectMode) => void;
  excludedAdIds: string[];
  setExcludedAdIds: (ids: string[]) => void;
  assigneeOptions: IdLabelValue[];
  setDateRange: (start: string, end: string) => void;
  setDateRangeType: (type: string) => void;
  dateRangeType: string;
  stuckDaysFilter: StuckDaysFilterState;
  setStuckDaysFilter: (filter: StuckDaysFilterState) => void;
  resultsCount: number;
  fetchAds: (searchTerm?: string, reset?: boolean) => void;
  loadMoreAds: () => void;
  adsHasMore: boolean;
  adsIsLoading: boolean;
  adsTotalCount: number;
  children?: ReactNode;
};

type ActionType =
  | { type: 'search' }
  | { type: 'filter'; payload: BuildListFilteredPayloadParams };

const LeadFilters = ({
  search,
  setSearch,
  sourceOptions,
  statusOptions,
  tagOptions,
  adOptions,
  selectedSources,
  selectedStatusIds,
  selectedTagIds,
  selectedAssigneeIds,
  selectedAdIds,
  setSelectedSources,
  setSelectedStatusIds,
  setSelectedTagIds,
  setSelectedAssigneeIds,
  setSelectedAdIds,
  adsSelectMode,
  setAdsSelectMode,
  excludedAdIds,
  setExcludedAdIds,
  assigneeOptions,
  setDateRange,
  setDateRangeType,
  dateRangeType,
  stuckDaysFilter,
  setStuckDaysFilter,
  resultsCount,
  fetchAds,
  loadMoreAds,
  adsHasMore,
  adsIsLoading,
  adsTotalCount,
  children
}: LeadFiltersProps) => {
  const dispatch = useDispatch();
  const { currentOrg, orgFeatureFlags } = useSelector(
    (state: any) => state.user
  );
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [action, setAction] = useState<ActionType | null>(null);
  const [adsFetched, setAdsFetched] = useState(false);

  const isMetaLeadIngestionEnabled = orgFeatureFlags?.includes(
    FEATURE_FLAGS.META_LEAD_INGESTION
  );

  const selectedFilters: SelectedFilterValues = {
    status: selectedStatusIds,
    source: selectedSources,
    ads: selectedAdIds,
    tags: selectedTagIds,
    assignee: selectedAssigneeIds,
    dateRange: dateRangeType ? [dateRangeType] : [],
    daysStuck: encodeDaysStuckFilter(stuckDaysFilter)
  };

  const handleAdsSearch = useCallback(
    (term: string) => fetchAds(term, true),
    [fetchAds]
  );

  const handleAdsSelect = useCallback(() => {
    if (!adsFetched) {
      fetchAds('', true);
      setAdsFetched(true);
    }
  }, [fetchAds, adsFetched]);

  const filterLists = {
    status: statusOptions,
    source: sourceOptions,
    ads: adOptions,
    tags: tagOptions,
    assignee: assigneeOptions,
    dateRange: mapDateRangeOptions()
  };

  // mapping options for each field
  const filteredFieldOptions = useMemo(
    () =>
      Object.entries(fieldOptions).reduce((acc, [key, fld]) => {
        if (key === 'ads' && !isMetaLeadIngestionEnabled) return acc;

        acc[key as keyof typeof fieldOptions] = {
          ...fld,
          ...(fld.panelType !== 'custom' && {
            options: filterLists[fld.field as keyof typeof filterLists]
          }),
          ...(key === 'daysStuck' && {
            CustomPanel: DaysStuckFilterPanel,
            getSelectedCount: getDaysStuckSelectedCount
          }),
          ...(key === 'ads' && {
            disabled: isAdsDisabled,
            disabledTooltip: 'Facebook ad not filtered in source',
            hasSearch: true,
            onSearch: handleAdsSearch,
            onSelect: handleAdsSelect,
            hasPagination: true,
            onLoadMore: loadMoreAds,
            isLoading: adsIsLoading,
            hasMore: adsHasMore,
            hasSelectAll: true,
            totalCount: adsTotalCount,
            isSelectAll: adsSelectMode === 'exclude',
            excludedValues: excludedAdIds
          })
        };
        return acc;
      }, {} as FieldOptionObjV2),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isMetaLeadIngestionEnabled,
      adsIsLoading,
      adsHasMore,
      adsSelectMode,
      excludedAdIds,
      adsTotalCount,
      statusOptions,
      sourceOptions,
      adOptions,
      tagOptions,
      assigneeOptions,
      handleAdsSearch,
      handleAdsSelect,
      loadMoreAds
    ]
  );

  const onFilterChange = (
    filters: SelectedFilterValues,
    selectAllModes?: SelectAllModes,
    excludedFilterItems?: SelectedFilterValues
  ) => {
    dispatch(setLeadListPage(0));
    setSelectedStatusIds(filters.status || []);
    setSelectedSources(filters.source || []);
    setSelectedTagIds(filters.tags || []);
    setSelectedAssigneeIds(filters.assignee || []);
    setDateRangeType(filters.dateRange?.[0] || '');

    const nextStuck = decodeDaysStuckFilter(filters.daysStuck);
    setStuckDaysFilter(isDaysStuckFilterComplete(nextStuck) ? nextStuck : null);

    const isAdsSelectAll = selectAllModes?.ads || false;
    if (isAdsSelectAll) {
      setAdsSelectMode('exclude');
      setExcludedAdIds(excludedFilterItems?.ads || []);
      setSelectedAdIds([]);
    } else {
      const mode = filters.ads?.length ? 'include' : 'none';
      setAdsSelectMode(mode);
      setSelectedAdIds(filters.ads || []);
      setExcludedAdIds([]);
    }

    if (!filters.source?.includes(LeadSource.FACEBOOK_ADS)) {
      setAdsFetched(false);
    }
  };

  const handleFilterChange = (
    filters: SelectedFilterValues,
    customDateRange: any,
    selectAllModes?: SelectAllModes,
    excludedFilterItems?: SelectedFilterValues
  ) => {
    const updatedFilters = { ...filters };

    if (!updatedFilters.source?.includes(LeadSource.FACEBOOK_ADS)) {
      updatedFilters.ads = [];
    }

    // Mixpanel payload variable
    const filterMixpanelPayload: BuildListFilteredPayloadParams = {
      status: updatedFilters.status,
      source: updatedFilters.source,
      tags: updatedFilters.tags,
      assignee: updatedFilters.assignee,
      dateRangeType: updatedFilters.dateRange?.[0]
    };

    // For date range, ensure only one option is selected
    // (radio button behavior)
    if (updatedFilters.dateRange && updatedFilters.dateRange.length > 1) {
      // Keep only the last selected option
      updatedFilters.dateRange = [
        updatedFilters.dateRange[updatedFilters.dateRange.length - 1]
      ];
    }

    // Handle date range selection separately
    if (
      !updatedFilters.dateRange ||
      updatedFilters.dateRange.length === 0 ||
      updatedFilters.dateRange[0] === ''
    ) {
      // No date range selected - send empty strings
      setDateRange('', '');
    } else {
      const selectedDateRangeType = updatedFilters.dateRange[0];
      // Get the appropriate date range based on selection
      const dateRange = getDateRangeForType(
        selectedDateRangeType,
        customDateRange
      );
      setDateRange(dateRange.startDate, dateRange.endDate);
      filterMixpanelPayload.dateRange = dateRange;
    }

    onFilterChange(updatedFilters, selectAllModes, excludedFilterItems);
    setAction({ type: 'filter', payload: filterMixpanelPayload });
  };

  const handleSelect = (
    filters: SelectedFilterValues,
    dateRange: CustomField[] | undefined,
    selectAllModes?: SelectAllModes,
    excludedFilterItems?: SelectedFilterValues
  ) => {
    // Handle custom date range from calendar
    let customDateRange = null;

    if (dateRange && dateRange.length > 0) {
      const dateField = dateRange.find((field) => field.key === 'dateRange');
      if (dateField && dateField.value) {
        customDateRange = {
          startDate: new Date(dateField.value.startDate),
          endDate: new Date(dateField.value.endDate)
        };
      }
    }
    handleFilterChange(
      filters,
      customDateRange,
      selectAllModes,
      excludedFilterItems
    );
  };

  const handleSearch = useCallback(
    (value: string) => {
      dispatch(setLeadListPage(0));
      setSearch(value);
      setAction({ type: 'search' });
    },
    [dispatch, setSearch]
  );

  // Mixpanel event handling
  useEffect(() => {
    if (!action) return;

    if (action.type === 'search') {
      sendMixpanelEvent(Event.LEAD_LIST_SEARCHED, {
        org_id: currentOrg?.id,
        org_name: currentOrg?.name,
        search_query: search,
        results_count: resultsCount
      });
    }

    if (action.type === 'filter') {
      sendMixpanelEvent(Event.LEAD_LIST_FILTERED, {
        org_id: currentOrg?.id,
        org_name: currentOrg?.name,
        ...buildListFilteredMixpanelPayload(action.payload),
        results_count: resultsCount
      });
    }

    setAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsCount]);

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-x-10">
        <SearchBar
          placeholder="Search"
          value={search}
          onSearch={handleSearch}
          searchBarStyle="w-full max-w-96"
        />
      </div>
      <div className="flex shrink-0 items-center gap-x-2">
        <MultiFieldFilterV2
          fieldOptions={filteredFieldOptions}
          onApply={handleSelect}
          selectedFilters={selectedFilters}
          type="multi"
          customFields={customFields}
          setCustomFields={setCustomFields}
        />
        {children}
      </div>
    </div>
  );
};

export default LeadFilters;
